import React from 'react';
import PageHero from './PageHero';
import {
  Globe, FlaskConical, Cpu, BookOpen, Trophy,
  Lightbulb, Target, Clock, Badge, Info,
  CheckCircle, Music, Dumbbell, Languages,
  Hash, Palette, Brain, GraduationCap, Award, TrendingUp, Microscope, Star
} from 'lucide-react';
import seniorImg from '../assets/SENIOR SCHOOL.png';
import middleImg from '../assets/JUNIOR SCHOOL.png';

function SectionTitle({ eyebrow, title, sub }) {
  return (
    <div className="text-center mb-16">
      {eyebrow && (
        <div className="inline-flex items-center gap-2 mb-4 px-4 py-1.5 rounded-full border border-gold/30 bg-gold/10">
          <span className="text-gold text-[11px] font-bold tracking-[0.2em] uppercase">{eyebrow}</span>
        </div>
      )}
      <h2 className="text-4xl md:text-5xl font-heading font-bold text-navy leading-tight uppercase">{title}</h2>
      {sub && <p className="text-ink/60 text-lg mt-4 max-w-2xl mx-auto">{sub}</p>}
    </div>
  );
}

function FeatureBox({ icon, title, desc, accent }) {
  const styles = {
    orange: { border: 'border-[#f97316]', bg: 'bg-[#fff7f0]', ic: 'text-[#f97316]' },
    blue: { border: 'border-[#3b82f6]', bg: 'bg-[#eff6ff]', ic: 'text-[#3b82f6]' },
    green: { border: 'border-[#22c55e]', bg: 'bg-[#f0fdf4]', ic: 'text-[#22c55e]' },
    red: { border: 'border-[#ef4444]', bg: 'bg-[#fef2f2]', ic: 'text-[#ef4444]' },
    pink: { border: 'border-[#ec4899]', bg: 'bg-[#fdf2f8]', ic: 'text-[#ec4899]' },
    purple: { border: 'border-[#a855f7]', bg: 'bg-[#faf5ff]', ic: 'text-[#a855f7]' },
    yellow: { border: 'border-[#eab308]', bg: 'bg-[#fefce8]', ic: 'text-[#eab308]' },
    violet: { border: 'border-[#8b5cf6]', bg: 'bg-[#f5f3ff]', ic: 'text-[#8b5cf6]' },
  }[accent];
  return (
    <div className={`rounded-2xl border-l-4 p-6 h-full shadow-sm hover:shadow-md transition-shadow duration-300 ${styles.border} ${styles.bg}`}>
      <div className={`mb-4 ${styles.ic}`}>{icon}</div>
      <h4 className="text-lg font-heading font-semibold text-navy mb-2">{title}</h4>
      <p className="text-ink/70 text-[15px] leading-relaxed">{desc}</p>
    </div>
  );
}

function SubjectFeature({ icon, title, desc, align = 'right' }) {
  const isLeft = align === 'left';
  return (
    <div className={`flex items-start gap-4 mb-10 ${isLeft ? 'flex-row-reverse text-right' : ''}`}>
      <div className="shrink-0 w-12 h-12 rounded-xl bg-navy flex items-center justify-center text-white shadow-md">{icon}</div>
      <div className={isLeft ? 'text-right' : ''}>
        <h3 className="text-lg font-heading font-semibold text-navy mb-1">{title}</h3>
        <p className="text-ink/70 text-[15px] leading-relaxed">{desc}</p>
      </div>
    </div>
  );
}

function InfoCard({ icon, iconBg, checkColor, title, items }) {
  return (
    <div className="bg-white rounded-2xl shadow-lg border border-line p-8 h-full">
      <div className="flex items-center gap-4 mb-6">
        <div className={`w-12 h-12 rounded-full ${iconBg} flex items-center justify-center shrink-0`}>{icon}</div>
        <h3 className="text-xl font-heading font-semibold text-navy">{title}</h3>
      </div>
      <ul className="space-y-4">
        {items.map((item, i) => (
          <li key={i} className="flex items-start gap-3">
            <CheckCircle size={18} className={`shrink-0 mt-0.5 ${checkColor}`} />
            <span className="text-ink/80 text-[15px] leading-relaxed">{item}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

// ── Pathway Cards ──────────────────────────────────────────────────────────────
function PathwayCard({ badge, title, sub, desc, items, badgeColor }) {
  return (
    <div className="bg-white rounded-2xl shadow-lg border border-line p-8 h-full">
      <div className={`inline-flex items-center gap-2 mb-5 px-4 py-1.5 rounded-full text-[11px] font-bold tracking-widest uppercase ${badgeColor}`}>
        {badge}
      </div>
      <h3 className="text-2xl font-heading font-bold text-navy mb-1">{title}</h3>
      <p className="text-gold font-semibold text-sm mb-4">{sub}</p>
      <p className="text-ink/70 text-[15px] leading-relaxed mb-6">{desc}</p>
      <ul className="space-y-3">
        {items.map((item, i) => (
          <li key={i} className="flex items-start gap-3">
            <CheckCircle size={16} className="shrink-0 mt-0.5 text-gold" />
            <span className="text-ink/80 text-[14px] leading-relaxed">{item}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function SeniorSchoolPage({ setPage, t }) {
  const navCategory = t?.nav?.[2]?.label || 'Academics';
  const homeLabel = t?.nav?.[0]?.label || 'Home';

  return (
    <div className="w-full bg-[#f8f9fa] min-h-screen">
      {/* ── Hero ─────────────────────────────────────────────────────────────── */}
      <PageHero
        title={"Senior School"}
        breadcrumbs={[homeLabel, navCategory, "Senior School"]}
        eyebrow={navCategory}
        subtitle={"IB DP & CP — Grade 11 to Grade 12"}
        bgImage={seniorImg}
      />


      {/* ── About ── */}
      <div className="max-w-[1200px] mx-auto px-8 py-24">
        <div className="grid md:grid-cols-2 gap-16 items-center">
          <div>
            <div className="inline-flex items-center gap-2 mb-5 px-4 py-1.5 rounded-full border border-gold/30 bg-gold/10">
              <span className="text-gold text-[11px] font-bold tracking-[0.2em] uppercase">IB DP & CP · Grades 11–12</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-heading font-bold text-navy mb-6 leading-tight uppercase">
              The Gateway to World-Class Universities
            </h2>
            <p className="text-ink/75 text-lg leading-relaxed mb-6">
              GMIS Jakarta's Senior School offers two prestigious two-year pathways:
              the <strong>IB Diploma Programme (DP)</strong> and the <strong>IB Career-related Programme (CP)</strong>.
              Both are globally recognised qualifications that open doors to leading universities and careers worldwide.
            </p>
            <p className="text-ink/75 text-lg leading-relaxed">
              Our students are guided by expert IB-trained faculty, rigorous academic preparation, and a deep commitment
              to developing reflective, ethical, and internationally-minded graduates ready for the next chapter.
            </p>
          </div>
          <div className="relative">
            <div className="absolute -top-4 -right-4 w-24 h-24 bg-gold/10 rounded-2xl" />
            <div className="absolute -bottom-4 -left-4 w-16 h-16 bg-navy/10 rounded-full" />
            <img src={middleImg} alt="GMIS Senior School" className="relative z-10 rounded-2xl shadow-2xl w-full h-[400px] object-cover object-center border-4 border-white" />
          </div>
        </div>
      </div>

      {/* ── Programme Pathways ── */}
      <div className="bg-white py-24">
        <div className="max-w-[1200px] mx-auto px-8">
          <SectionTitle eyebrow="Choose Your Path" title="Senior School Pathways" sub="Two internationally recognised two-year programmes for Grades 11 & 12" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <PathwayCard
              badge="IB Diploma Programme"
              badgeColor="bg-navy/10 text-navy"
              title="IB Diploma (DP)"
              sub="The gold standard for university preparation"
              desc="The IB DP is a rigorous, academically challenging programme recognised by the world's top universities. Students study six subjects across different disciplines, complete an Extended Essay, Theory of Knowledge (TOK), and Creativity, Activity, Service (CAS)."
              items={[
                "Six subject groups — Languages, Sciences, Mathematics, Humanities, Arts, and Electives.",
                "Extended Essay (EE) — an independent 4,000-word research paper on a topic of the student's choice.",
                "Theory of Knowledge (TOK) — critical thinking and the nature of knowledge explored across disciplines.",
                "CAS (Creativity, Activity, Service) — holistic growth through projects, sports, and community work.",
                "Maximum of 45 points — the globally recognised gold standard for university entrance.",
              ]}
            />
            <PathwayCard
              badge="IB Career-related Programme"
              badgeColor="bg-gold/20 text-[#8b6914]"
              title="IB Career-Related Programme (CP)"
              sub="Academic excellence with vocational depth"
              desc="The IB CP combines the rigour of IB coursework with practical, career-oriented learning. Students pursue two DP subjects alongside a career-related study, developing both academic and professional competencies."
              items={[
                "Two IB Diploma subjects combined with a career-related study pathway.",
                "Personal and Professional Skills (PPS) — workplace readiness and self-development.",
                "Reflective Project — an in-depth piece of work based on an ethical dilemma in a career field.",
                "Language Development and Service Learning integrated across the programme.",
                "Ideal for students with a clear career direction in business, design, technology, or healthcare.",
              ]}
            />
          </div>
        </div>
      </div>

      {/* ── Facilities ── */}
      <div className="py-24">
        <div className="max-w-[1200px] mx-auto px-8">
          <SectionTitle eyebrow="Campus & Resources" title="Facilities & Special Programs" />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <FeatureBox accent="orange" icon={<GraduationCap size={28} />}
              title="University Counselling"
              desc="Dedicated university guidance counsellors supporting applications to leading global universities." />
            <FeatureBox accent="blue" icon={<Microscope size={28} />}
              title="Advanced Science Labs"
              desc="Fully equipped HL-level biology, chemistry, and physics labs for extended experimental work." />
            <FeatureBox accent="green" icon={<Award size={28} />}
              title="EE & TOK Support"
              desc="Structured support for the Extended Essay and Theory of Knowledge from dedicated supervisors." />
            <FeatureBox accent="red" icon={<TrendingUp size={28} />}
              title="University Track Record"
              desc="Consistent acceptances at leading universities in the US, UK, EU, Australia, and Asia." />
            <FeatureBox accent="pink" icon={<Trophy size={28} />}
              title="MUN & Academic Teams"
              desc="Model UN, debate, maths olympiad, and science competitions — preparing global leaders." />
            <FeatureBox accent="purple" icon={<Music size={28} />}
              title="Arts & Exhibitions"
              desc="Visual arts, theatre, and music showcases featuring senior student-led creative works." />
            <FeatureBox accent="yellow" icon={<Brain size={28} />}
              title="Research & Innovation"
              desc="Student research projects, guest lecturers, and university partnership programmes." />
            <FeatureBox accent="violet" icon={<Globe size={28} />}
              title="Global Campus Network"
              desc="Access to GMIS's Bali and Semarang campuses, exchange programmes, and global events." />
          </div>
        </div>
      </div>

      {/* ── DP Subject Groups ── */}
      <div className="bg-white py-24">
        <div className="max-w-[1200px] mx-auto px-8">
          <SectionTitle eyebrow="IB DP Subject Groups" title="Diploma Programme Subjects" sub="Six subject groups — students select one from each plus HL/SL choices" />
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 items-center">
            <div>
              <SubjectFeature align="left" icon={<Languages size={20} />}
                title="Group 1: Language & Literature"
                desc="English Language and Literature — analysing texts, media, and language in cultural contexts." />
              <SubjectFeature align="left" icon={<Globe size={20} />}
                title="Group 2: Language Acquisition"
                desc="Bahasa Indonesia, Mandarin, French — reaching B1–B2 proficiency in a second language." />
              <SubjectFeature align="left" icon={<Globe size={20} />}
                title="Group 3: Individuals & Societies"
                desc="Business Management, Economics, History, Psychology, or Geography at SL or HL." />
            </div>
            <div className="flex items-center justify-center">
              <div className="relative">
                <div className="absolute inset-0 bg-gold/20 rounded-full blur-3xl scale-110" />
                <img src={seniorImg} alt="Senior at GMIS" className="relative z-10 rounded-2xl shadow-2xl w-full max-w-[320px] h-[440px] object-cover object-top mx-auto border-4 border-white" />
              </div>
            </div>
            <div>
              <SubjectFeature align="right" icon={<FlaskConical size={20} />}
                title="Group 4: Sciences"
                desc="Biology, Chemistry, Physics, Computer Science, or Environmental Systems at SL or HL." />
              <SubjectFeature align="right" icon={<Hash size={20} />}
                title="Group 5: Mathematics"
                desc="Mathematics: Analysis & Approaches or Mathematics: Applications & Interpretation at SL or HL." />
              <SubjectFeature align="right" icon={<Palette size={20} />}
                title="Group 6: Arts or Elective"
                desc="Visual Arts, Music, Theatre, Film, or a second subject from Groups 1–4 as the sixth choice." />
            </div>
          </div>
        </div>
      </div>

      {/* ── Info Cards ── */}
      {/* <div className="bg-[#f8f9fa] py-24">
        <div className="max-w-[1200px] mx-auto px-8">
          <SectionTitle eyebrow="Programme Details" title="Senior School Programme Overview" sub="Grades 11 to 12 — Launching the Next Generation of Global Leaders" />
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
            <InfoCard
              icon={<Lightbulb size={22} className="text-blue-600" />} iconBg="bg-blue-50" checkColor="text-blue-500"
              title="Approach & Teaching Methodology"
              items={[
                "IB DP/CP concept-driven, inquiry-led instruction by specialist subject teachers.",
                "Higher Level (HL) and Standard Level (SL) choices tailored to each student's university pathway.",
                "TOK and EE integrated across the curriculum with dedicated supervisors for each student.",
                "University-style seminars, research presentations, and collaborative academic projects.",
                "Internal and external IB assessments alongside regular mock examinations.",
                "Individualised academic target setting and university application support from Grade 11.",
              ]}
            />
            <InfoCard
              icon={<Target size={22} className="text-green-600" />} iconBg="bg-green-50" checkColor="text-green-500"
              title="Programme Focus"
              items={[
                "Securing IB Diploma or CP certification — recognised by 5,000+ universities worldwide.",
                "Personalised university guidance for applications to the UK, US, EU, Australia, and Asia.",
                "CAS and Service Learning — real-world projects that build character and global citizenship.",
                "Research skills, academic writing, and independent thinking developed throughout Grade 11–12.",
                "Strong alumni network supporting mentorship, internship, and career exploration opportunities.",
              ]}
            />
            <InfoCard
              icon={<Clock size={22} className="text-cyan-600" />} iconBg="bg-cyan-50" checkColor="text-cyan-500"
              title="School Timings"
              items={[
                "Monday to Friday: 7:30 AM – 3:30 PM",
                "After-school study sessions and university prep workshops until 5:30 PM.",
                "Supervised study halls and library access for Extended Essay research.",
                "Mock examinations held each semester aligned with the IB examination calendar.",
              ]}
            />
            <InfoCard
              icon={<Badge size={22} className="text-amber-600" />} iconBg="bg-amber-50" checkColor="text-amber-500"
              title="Uniform & Dress Code"
              items={[
                "Senior students wear the formal GMIS uniform — a mark of pride and achievement.",
                "Separate PE/sports attire for physical education and CAS activity sessions.",
                "Formal uniform required for examinations, university visits, and school events.",
                "Senior students are role models for the school community and expected to uphold standards.",
              ]}
            />
          </div>
          <div className="bg-[#fef8f0] border border-gold/30 rounded-2xl p-8 shadow-sm mt-8">
            <h3 className="text-lg font-heading font-bold text-navy mb-5 flex items-center gap-3">
              <Star size={18} className="text-gold" /> A Note to Parents
            </h3>
            <div className="flex flex-col gap-3">
              {[
                "Senior school is a pivotal two years. Your encouragement, emotional support, and trust in your child's abilities are among the most powerful factors in their success.",
                "University applications require early planning. Engage with our university counsellors from the start of Grade 11 to explore options, shortlist universities, and prepare strong applications.",
                "Manage exam stress as a family. Ensure your child has healthy sleep, nutrition, and downtime. Academic excellence and wellbeing go hand in hand — both matter deeply at GMIS.",
              ].map((text, i) => (
                <div key={i} className="flex items-start gap-2.5">
                  <div className="w-4 h-4 rounded-full bg-gold/30 flex items-center justify-center shrink-0 mt-0.5">
                    <div className="w-1.5 h-1.5 rounded-full bg-gold" />
                  </div>
                  <p className="text-ink/75 text-[14px] leading-relaxed">{text}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div> */}

      {/* ── CTA ── */}
      <div className="w-full bg-white">
        <div className="max-w-[1200px] mx-auto px-8 py-12">
        <div className="relative overflow-hidden bg-navy rounded-3xl p-12 md:p-20 text-center shadow-2xl">
          <div className="relative z-10">
            <h3 className="text-4xl md:text-5xl font-heading font-bold text-white mb-6 leading-tight uppercase">
              Your Child's Future Starts Here
            </h3>
            <p className="text-white/70 text-xl max-w-2xl mx-auto mb-10">
              Discover how GMIS's Senior School prepares students for leading universities and a life of purpose.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-4">
              <button onClick={() => { if (setPage) { setPage('contact'); window.scrollTo(0, 0); } }}
                className="bg-gold text-navy px-10 py-4 rounded-full font-bold text-lg tracking-wide hover:bg-white hover:text-navy transition-colors duration-300">
                Schedule a Visit
              </button>
              <button onClick={() => { if (setPage) { setPage('coming-soon'); window.scrollTo(0, 0); } }}
                className="bg-white/10 text-white px-10 py-4 rounded-full font-bold text-lg tracking-wide hover:bg-white/20 transition-colors duration-300 border border-white/20">
                Apply Now
              </button>
            </div>
          </div>
        </div>
        </div>
      </div>
    </div>
  );
}
